import React, { useMemo, useState } from "react";
import { Sparkles, Shirt, MapPinned } from "lucide-react";
import data from "../../content/artworkSizing.json";
import SizeResultCard from "./SizeResultCard";
import {
  ArtworkSizingData,
  findGarment,
  findLocation,
  getRecommendedSize,
} from "../../utils/sizePlacement";

const sizingData = data as ArtworkSizingData;

export default function SizePlacementAssistant() {
  const [garmentId, setGarmentId] = useState(sizingData.garments[0]?.id || "");
  const [locationId, setLocationId] = useState(
    sizingData.garments[0]?.locations[0]?.id || ""
  );

  const garment = useMemo(() => {
    return findGarment(sizingData, garmentId);
  }, [garmentId]);

  const locations = garment?.locations || [];

  const location = useMemo(() => {
    return findLocation(garment, locationId) || locations[0] || null;
  }, [garment, locationId, locations]);

  const recommendedSize = useMemo(() => {
    if (!location) return null;
    return getRecommendedSize(location.sizes);
  }, [location]);

  const handleGarmentChange = (value: string) => {
    setGarmentId(value);

    const nextGarment = findGarment(sizingData, value);
    const nextDefaultLocation = nextGarment?.locations?.[0] || null;

    setLocationId(nextDefaultLocation?.id || "");
  };

  return (
    <section>
      <div className="mb-6">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
          <Sparkles className="h-4 w-4" />
          Sizing Tool
        </div>

        <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
          Size & Placement Assistant
        </h2>

        <p className="mt-3 max-w-3xl text-sm leading-6 text-dtf-text-muted md:text-base">
          Pick a garment and print location to get recommended DTF artwork size,
          placement guidance, warnings, and a result that can later connect into
          your mockup tool.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
          <label className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
            <Shirt className="h-4 w-4 text-dtf-blue" />
            Garment Type
          </label>

          <select
            value={garmentId}
            onChange={(e) => handleGarmentChange(e.target.value)}
            className="w-full rounded-2xl border border-white/10 bg-[#08111f] px-4 py-3 text-white outline-none transition focus:border-dtf-blue/40"
          >
            {sizingData.garments.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
          <label className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
            <MapPinned className="h-4 w-4 text-dtf-blue" />
            Print Location
          </label>

          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="w-full rounded-2xl border border-white/10 bg-[#08111f] px-4 py-3 text-white outline-none transition focus:border-dtf-blue/40"
          >
            {locations.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6">
        {garment && location && recommendedSize ? (
          <SizeResultCard
            garmentLabel={garment.label}
            locationLabel={location.label}
            placementType={location.placementType}
            size={recommendedSize}
            placement={location.placements}
            warnings={location.warnings}
          />
        ) : (
          <div className="rounded-[24px] border border-dashed border-white/10 bg-black/20 p-6 text-sm text-dtf-text-muted">
            No sizing data found for this selection.
          </div>
        )}
      </div>
    </section>
  );
}